import { useEffect, useState } from 'react';
import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { Switch, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { LockKeyhole, SquarePen } from 'lucide-react';
import { useTranslate } from '@tolgee/react';
import { ReceptionModeTags } from './ReceptionModeTags';
import HighlightText from 'views/PureLLM/Components/HighlightText';
import TemplateText from 'views/PureLLM/Components/TemplateText';
import type { ApiReceptionMode, ContentParameter } from '../../../Types/api';
import TriggerConditionList from './TriggerConditionList';
import type { TriggerCondition } from './TriggerConditionList';
import { ReactComponent as BranchSvg } from 'views/PureLLM/assets/branch.svg';
import { ReactComponent as MessageSvg } from 'views/PureLLM/assets/message.svg';
import AppliedStores from './AppliedStores';

export interface BranchCardProps {
  /** 分支唯一标识 */
  policyId?: string;
  /** 序号 */
  index: number;
  /** 分支标题 */
  title: string;
  /** 是否启用 */
  enabled: boolean;
  /** 触发条件列表 */
  triggerConditions: TriggerCondition[];
  /** 回复逻辑描述 */
  replyLogicDesc: string;
  /** 回复示例内容 */
  replyExample: string;
  /** 固定回复文本内容 */
  fixedReplyContent?: string;
  /** 接待模式 */
  receptionMode?: ApiReceptionMode;
  /** 待办标签名 */
  todoLabel?: string;
  /** 待办类型值 */
  todoType?: string;
  /** 出厂接待模式 */
  originalReceptionMode?: ApiReceptionMode;
  /** 是否为预设回复（固定回复） */
  isFixedReply?: boolean;
  /** 切换启用状态 */
  onToggle?: (enabled: boolean) => void;
  /** 开关提交中 */
  toggleLoading?: boolean;
  /** 点击修改接待模式 */
  onModifyReceptionMode?: () => void;
  /** 点击反馈优化建议 */
  onFeedbackClick?: () => void;
  /** 是否支持收起（仅展示标题行） */
  collapsible?: boolean;
  /** 受控：是否展开；不传则由内部 state / defaultExpanded 决定 */
  expanded?: boolean;
  /** 非受控时初始是否展开 */
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** 命中场景：一次性背景强调动画（总时长 1500ms，结束后底色 #FFEFD8） */
  playHitHighlight?: boolean;
  /** 列表/弹窗等场景：整体左右留白（如 Tailwind px-4） */
  listHorizontalPadding?: boolean;
  /** 门店范围信息 */
  storeScope?: {
    allStore: boolean;
    storeInfo?: Array<{
      storeId?: string | number;
      storeName?: string;
      alias?: string;
      externalType?: string;
      region?: string;
      [key: string]: unknown;
    }>;
  };
  /** 子分支 */
  children?: BranchCardData[];
  /** 区分父分支 / 子分支 header 颜色 */
  headerVariant?: 'default' | 'sub';
  /** 是否展示开关 */
  showToggle?: boolean;
  /** 外层命中态需要向两侧扩展的距离 */
  highlightBleedX?: number;
  /** 是否允许编辑 */
  editable?: boolean;
  /** 搜索关键词（用于高亮） */
  searchKeyword?: string;
  /** 模板参数映射 (name → ContentParameter) */
  parameterMap?: Record<string, ContentParameter>;
}

export interface BranchCardData extends BranchCardProps {
  policyId: string;
  togglePolicyId?: string;
  policyTriggerConditions?: TriggerCondition[];
}

/** 命中分支高亮：0–150ms 闪入 → 750ms 峰值 → 600ms 过渡至常驻色 */
const branchHitHighlight = keyframes`
  0% {
    background-color: #ffffff;
    animation-timing-function: ease-out;
  }
  10% {
    background-color: #ffd9a8;
  }
  10.01% {
    background-color: #ffd9a8;
    animation-timing-function: linear;
  }
  60% {
    background-color: #ffd9a8;
    animation-timing-function: ease-in-out;
  }
  100% {
    background-color: #ffefd8;
  }
`;

/** 列表区：垂直方向用 padding 留白；命中高亮动画加在该层（data-sid=bc-listpad-36） */
const ListPadWrap = styled.div<{ $playHitHighlight?: boolean; $highlightBleedX?: number }>`
  box-sizing: border-box;
  margin-left: ${({ $highlightBleedX = 0 }) => ($highlightBleedX ? `-${$highlightBleedX}px` : 0)};
  margin-right: ${({ $highlightBleedX = 0 }) => ($highlightBleedX ? `-${$highlightBleedX}px` : 0)};
  padding-left: ${({ $highlightBleedX = 0 }) => ($highlightBleedX ? `${$highlightBleedX}px` : 0)};
  padding-right: ${({ $highlightBleedX = 0 }) => ($highlightBleedX ? `${$highlightBleedX}px` : 0)};
  ${({ $playHitHighlight }) =>
    $playHitHighlight
      ? css`
          animation: ${branchHitHighlight} 1.5s forwards;
        `
      : ''}
`;
const CardRoot = styled.div<{ $omitBottomMargin?: boolean }>`
  margin-bottom: ${(p) => (p.$omitBottomMargin ? 0 : '1rem')};
  border-radius: 4px;
  border: 1px solid var(--theme-border-2, #f0f0f0);
  background-color: var(--fett-bg-panel);
`;

function BranchCard(props: BranchCardProps) {
  const { t } = useTranslate();
  const {
    policyId,
    index,
    title,
    enabled,
    triggerConditions,
    replyLogicDesc,
    replyExample,
    fixedReplyContent,
    receptionMode,
    todoLabel,
    isFixedReply = false,
    onToggle,
    toggleLoading = false,
    onModifyReceptionMode,
    collapsible = false,
    expanded: expandedProp,
    defaultExpanded = true,
    onExpandedChange,
    playHitHighlight = false,
    listHorizontalPadding = false,
    storeScope,
    children,
    headerVariant = 'default',
    showToggle = true,
    highlightBleedX = 0,
    editable = true,
    searchKeyword,
    parameterMap,
  } = props;

  const isControlled = expandedProp !== undefined;
  const [innerExpanded, setInnerExpanded] = useState(defaultExpanded);
  const shouldAutoExpandChildren =
    Boolean(children?.some((child) => child.playHitHighlight)) ||
    Boolean(
      searchKeyword?.trim() &&
        children?.some((child) =>
          [child.title, child.replyLogicDesc, child.replyExample, child.fixedReplyContent]
            .filter((v): v is string => typeof v === 'string')
            .some((v) => v.toLowerCase().includes(searchKeyword!.trim().toLowerCase())),
        ),
    );
  const [childrenExpanded, setChildrenExpanded] = useState(shouldAutoExpandChildren);
  const showBody = !collapsible || (isControlled ? expandedProp! : innerExpanded);
  const hasNestedChildren = Boolean(children?.length);

  useEffect(() => {
    if (shouldAutoExpandChildren) {
      setChildrenExpanded(true);
    }
  }, [shouldAutoExpandChildren]);

  const toggleExpand = () => {
    if (showBody) return;
    const next = true;
    if (!isControlled) {
      setInnerExpanded(next);
    }
    onExpandedChange?.(next);
  };

  const headerRadius = collapsible && !showBody ? '4px' : '4px 4px 0 0';

  const needsListPadWrap = listHorizontalPadding || playHitHighlight;
  const normalizedReplyLogicDesc = replyLogicDesc.trim();
  const normalizedReplyContent = (isFixedReply ? fixedReplyContent : replyExample)?.trim() ?? '';
  const shouldShowReplyTitle = Boolean(normalizedReplyLogicDesc);
  const shouldShowReplyContent = Boolean(normalizedReplyContent);
  const shouldShowReplySection =
    shouldShowReplyTitle || shouldShowReplyContent || Boolean(children?.length);
  const appliedStoreCount = storeScope?.allStore ? 0 : storeScope?.storeInfo?.length ?? 0;
  const shouldShowAppliedStores = showToggle && appliedStoreCount > 0;
  const headerBackground =
    headerVariant === 'sub'
      ? 'linear-gradient(180deg, var(--fett-scenario-success-soft-bg) 0%, var(--fett-bg-panel) 100%)'
      : 'linear-gradient(180deg, var(--fett-scenario-header-bg) 0%, var(--fett-bg-panel) 100%)';
  const indexClassName =
    headerVariant === 'sub'
      ? 'flex items-center justify-center w-5 h-5 flex-shrink-0 font-medium text-xs text-[var(--fett-scenario-success-text)] text-center leading-[18px] rounded bg-[var(--fett-scenario-success-bg)]'
      : 'flex items-center justify-center w-5 h-5 flex-shrink-0 font-medium text-xs text-[var(--fett-scenario-accent-text)] text-center leading-[18px] rounded bg-[var(--fett-scenario-accent-strong-bg)]';

  const card = (
    <CardRoot
      data-sid="bc-root-01"
      data-branch-policy-id={needsListPadWrap ? undefined : policyId}
      className="overflow-hidden"
      $omitBottomMargin={needsListPadWrap}
    >
      {/* 标题行 */}
      <div
        data-sid="bc-header-02"
        className="flex items-center justify-between px-4 py-3 gap-2"
        style={{
          backgroundImage: headerBackground,
          boxShadow: showBody ? 'inset 0 -1px 0 0 var(--fett-border-default)' : undefined,
          borderRadius: headerRadius,
        }}
      >
        <div data-sid="bc-titlewrap-03" className="flex items-center gap-2 min-w-0 flex-1">
          <span data-sid="bc-index-04" className={indexClassName}>
            {index}
          </span>
          <span
            data-sid="bc-title-05"
            className="text-sm font-medium truncate"
            style={{
              color: enabled ? 'var(--fett-text-primary)' : 'var(--fett-text-tertiary)',
            }}
          >
            <HighlightText text={title} keyword={searchKeyword} />
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {collapsible && !showBody && (
            <Button
              data-sid="bc-expand-34"
              type="link"
              size="small"
              className="!p-0 flex items-center gap-0.5"
              onClick={toggleExpand}
            >
              {t('scene.aiAgentConfig.scenarioDetail.action.expand')}{' '}
              <DownOutlined className="text-[10px]" />
            </Button>
          )}
          {shouldShowAppliedStores ? (
            <AppliedStores count={appliedStoreCount} storeInfo={storeScope?.storeInfo ?? []} />
          ) : null}
          {showToggle ? (
            <Switch
              data-sid="bc-switch-06"
              checked={enabled}
              loading={toggleLoading}
              disabled={toggleLoading || !editable}
              onChange={(checked, event) => {
                event?.stopPropagation?.();
                onToggle?.(checked);
              }}
            />
          ) : null}
        </div>
      </div>

      {/* 内容区 */}
      {showBody && (
        <div data-sid="bc-body-07" className="p-4">
          {/* 触发条件 */}
          {triggerConditions.length ? (
            <div data-sid="bc-trigger-08" className="flex mb-4">
              <div
                data-sid="bc-triglabel-09"
                className="w-20 flex-shrink-0"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  color: 'var(--fett-text-tertiary)',
                  lineHeight: '32px',
                }}
              >
                {t('scene.aiAgentConfig.scenarioDetail.label.triggerCondition')}
              </div>
              <TriggerConditionList
                conditions={triggerConditions}
                operatorDataSid="bc-trigand-12"
                itemDataSid="bc-trigitem-11"
                tagDataSid="bc-trigtag-13"
                overflowTriggerDataSid="bc-trigmore-47"
                overflowPopoverDataSid="bc-trigpopover-48"
              />
            </div>
          ) : null}

          {shouldShowReplySection ? (
            <div data-sid="bc-reply-14" className="flex mb-4">
              <div
                data-sid="bc-replylabel-15"
                className="w-20 flex-shrink-0"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  color: 'var(--fett-text-tertiary)',
                  lineHeight: '32px',
                }}
              >
                {t('scene.aiAgentConfig.scenarioDetail.label.replyLogic')}
              </div>
              <div data-sid="bc-replybody-16" className="flex-1">
                {shouldShowReplyTitle ? (
                  <div
                    data-sid="bc-replytitle-17"
                    className="mb-2 inline-flex items-center px-2"
                    style={{
                      height: 32,
                      background: 'var(--fett-bg-subtle)',
                      borderRadius: 4,
                      fontWeight: 500,
                      fontSize: 14,
                      color: 'var(--fett-text-primary)',
                    }}
                  >
                    <HighlightText text={normalizedReplyLogicDesc} keyword={searchKeyword} />
                  </div>
                ) : null}
                {shouldShowReplyContent ? (
                  <div
                    data-sid="bc-replybox-18"
                    className="rounded bg-[var(--fett-scenario-accent-bg)] p-[12px]"
                  >
                    <div
                      data-sid="bc-exheader-19"
                      className="flex items-center justify-between mb-2"
                    >
                      {isFixedReply ? (
                        <div className="flex items-center gap-1">
                          <LockKeyhole
                            data-sid="bc-lockicon-31"
                            size={14}
                            className="text-[var(--fett-text-primary)]"
                          />
                          <span
                            data-sid="bc-fixedlabel-32"
                            className="text-xs text-[var(--fett-text-primary)]"
                          >
                            {t('scene.aiAgentConfig.scenarioDetail.label.presetReply')}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Icon
                            data-sid="bc-exdot-20"
                            component={MessageSvg}
                            style={{ fontSize: 16 }}
                          />
                          <span
                            data-sid="bc-exlabel-21"
                            style={{
                              fontWeight: 500,
                              fontSize: 12,
                              color: 'var(--fett-text-primary)',
                              lineHeight: '18px',
                            }}
                          >
                            {t('scene.aiAgentConfig.scenarioDetail.label.replyExample')}
                          </span>
                          <span
                            data-sid="bc-exhint-22"
                            style={{
                              fontWeight: 400,
                              fontSize: 12,
                              color: 'var(--fett-text-quaternary)',
                              lineHeight: '18px',
                            }}
                          >
                            {t(
                              'scene.aiAgentConfig.scenarioDetail.description.replyExampleGenerated',
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    <div data-sid="bc-excontent-23" className="pl-5 text-sm text-gray-700">
                      <TemplateText
                        text={normalizedReplyContent}
                        keyword={searchKeyword}
                        parameterMap={parameterMap}
                      />
                    </div>
                  </div>
                ) : null}

                {children?.length ? (
                  <div
                    data-sid="bc-children-37"
                    className="mt-3 rounded-[4px] bg-[var(--fett-bg-subtle)] p-3"
                  >
                    <div
                      data-sid="bc-children-label-38"
                      className="flex items-center justify-between gap-3"
                      style={{ marginBottom: childrenExpanded ? 12 : 0 }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          data-sid="bc-children-text-49"
                          className="inline-flex items-center gap-1"
                          style={{
                            fontWeight: 500,
                            fontSize: 14,
                            color: 'var(--fett-text-primary)',
                            lineHeight: '18px',
                          }}
                        >
                          <BranchSvg
                            data-sid="bc-children-icon-50"
                            className="h-3.5 w-3.5 flex-shrink-0"
                          />
                          {t('scene.aiAgentConfig.scenarioDetail.label.multipleCases')}
                        </div>
                        <div
                          data-sid="bc-children-desc-51"
                          className="text-left"
                          style={{
                            fontWeight: 400,
                            fontSize: 12,
                            color: 'var(--fett-text-quaternary)',
                            lineHeight: '18px',
                          }}
                        >
                          {t('scene.aiAgentConfig.scenarioDetail.description.multipleCasesRouting')}
                        </div>
                      </div>
                      <Button
                        data-sid="bc-children-toggle-40"
                        type="link"
                        size="small"
                        className="!p-0 flex items-center gap-0.5"
                        onClick={() => setChildrenExpanded((prev) => !prev)}
                      >
                        {childrenExpanded
                          ? t('scene.aiAgentConfig.scenarioDetail.action.collapse')
                          : t('scene.aiAgentConfig.scenarioDetail.action.expand')}
                        <DownOutlined
                          className="text-[10px]"
                          style={{
                            transform: childrenExpanded ? 'rotate(180deg)' : undefined,
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </Button>
                    </div>
                    {childrenExpanded ? (
                      <div data-sid="bc-children-list-39" className="flex flex-col">
                        {children.map((child) => (
                          <BranchCard
                            key={child.policyId}
                            {...child}
                            headerVariant="sub"
                            showToggle={false}
                            highlightBleedX={12}
                            editable={editable}
                            parameterMap={parameterMap}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* 接待模式 */}
          {!hasNestedChildren && receptionMode ? (
            <div data-sid="bc-mode-24" className="flex">
              <div
                data-sid="bc-modelabel-25"
                className="w-20 flex-shrink-0"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  color: 'var(--fett-text-tertiary)',
                  lineHeight: '32px',
                }}
              >
                {t('scene.aiAgentConfig.scenarioDetail.label.receptionMode')}
              </div>
              <div
                data-sid="bc-modebody-26"
                className="flex items-center justify-between flex-1 gap-3"
              >
                <div data-sid="bc-modeinfo-27" className="min-w-0">
                  <ReceptionModeTags value={receptionMode} todoLabel={todoLabel} />
                </div>
                <Button
                  data-sid="bc-modebtn-30"
                  type="link"
                  className="!p-0"
                  disabled={!editable}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 'auto' }}
                  icon={<SquarePen size={14} style={{ display: 'block' }} />}
                  onClick={onModifyReceptionMode}
                >
                  {t('scene.aiAgentConfig.scenarioDetail.action.modifyReceptionMode')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </CardRoot>
  );

  if (needsListPadWrap) {
    const padClass = [listHorizontalPadding ? 'px-4' : '', 'pt-2', 'pb-2']
      .filter(Boolean)
      .join(' ');
    return (
      <ListPadWrap
        data-sid="bc-listpad-36"
        data-branch-policy-id={policyId}
        className={padClass}
        $playHitHighlight={playHitHighlight}
        $highlightBleedX={highlightBleedX}
      >
        {card}
      </ListPadWrap>
    );
  }

  return card;
}

export default BranchCard;
